/**
 * Prepare Fields Utility
 */

import { FieldEntityProps, SelectOptions, HiddenFields } from '../types';

export interface PrepareFieldsResult {
  selectOptions: SelectOptions[];
  dependentSelectOptions: SelectOptions[];
  hiddenFields: HiddenFields[];
}

/**
 * Sort comparator for weight-based ordering (lower weights first).
 */
const sortByWeight = (
  a: { weight: number | null },
  b: { weight: number | null },
): number => {
  if (a.weight !== null && b.weight !== null) {
    return a.weight - b.weight;
  }
  return -1;
};

/**
 * Merges menu options for items with the same selection key.
 */
const mergeMenuOptions = (items: SelectOptions[]): SelectOptions[] => {
  const merged: SelectOptions[] = [];

  for (const item of items) {
    const existing = merged.find((m) => m.selection === item.selection);
    if (existing) {
      existing.menuOptions = [...existing.menuOptions, ...item.menuOptions];
    } else {
      merged.push({
        selection: item.selection,
        menuOptions: [...item.menuOptions],
      });
    }
  }

  return merged;
};

/**
 * Extracts select options from SELECT type fields.
 */
const getSelectFieldOptions = (fields: FieldEntityProps[]): SelectOptions[] => {
  const options: SelectOptions[] = [];

  for (const field of fields) {
    if (field.fieldType === 'SELECT' && field.fieldValues) {
      options.push({
        selection: field.parameterName,
        menuOptions: field.fieldValues.map((v) => v.value),
      });
    }
  }

  return mergeMenuOptions(options);
};

/**
 * Extracts dependent select options from DEPENDENT_SELECT type fields.
 * Maps parent field values to their dependent options.
 */
const getDependentSelectOptions = (
  fields: FieldEntityProps[],
): SelectOptions[] => {
  const options: SelectOptions[] = [];

  for (const field of fields) {
    if (field.fieldType === 'DEPENDENT_SELECT' && field.fieldValues) {
      for (const value of field.fieldValues) {
        if (value.dependentField && value.dependentFieldValue) {
          options.push({
            selection: value.dependentFieldValue.value,
            menuOptions: [value.value],
          });
        }
      }
    }
  }

  return mergeMenuOptions(options);
};

/**
 * Extracts hidden fields with their parameter names and values.
 */
const getHiddenFields = (fields: FieldEntityProps[]): HiddenFields[] => {
  return fields
    .filter((field) => field.isHidden)
    .map((field) => ({
      parameterName: field.parameterName,
      parameterValue: field.fieldValues?.[0]?.value || '',
    }));
};

/**
 * Prepares field data by sorting fields and extracting options.
 *
 * This function:
 * - Sorts fields by weight (lower weights first)
 * - Sorts field values by weight
 * - Extracts SELECT field options
 * - Extracts DEPENDENT_SELECT field options
 * - Extracts hidden fields
 *
 * @param fields - Array of field entities from form data
 * @returns Object containing selectOptions, dependentSelectOptions, and hiddenFields
 *
 * @example
 * const { selectOptions, hiddenFields, dependentSelectOptions } = prepareFields(fields);
 */
export const prepareFields = (
  fields: FieldEntityProps[],
): PrepareFieldsResult => {
  // Sort fields by weight
  fields.sort(sortByWeight);

  // Sort field values by weight
  fields.forEach((field) => {
    if (field.fieldValues) {
      field.fieldValues.sort(sortByWeight);
    }
  });

  return {
    selectOptions: getSelectFieldOptions(fields),
    dependentSelectOptions: getDependentSelectOptions(fields),
    hiddenFields: getHiddenFields(fields),
  };
};
